import { PlatformSubscriptionStatus } from "@pilatestime/shared-types";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { fetchAccessProfileQueryOptions } from "@/api/accessProfile";
import { fetchUserOrganizationsQueryOptions } from "@/api/organizations";
import { ErrorPage } from "@/components/ErrorPage";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { useApiParameters } from "@/hooks/useApiParameters";
import { OrganizationInactive } from "./$orgId/-components/OrganizationInactive";
import { OrgBaseLayout } from "./$orgId/-components/OrgBaseLayout";

export const Route = createFileRoute("/_auth/app/_app/org/$orgId")({
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const orgId = params.orgId;

		// Ensure access profile and organizations are cached before rendering
		await Promise.all([
			context.queryClient.ensureQueryData(
				fetchAccessProfileQueryOptions(userId, token, orgId),
			),
			context.queryClient.ensureQueryData(
				fetchUserOrganizationsQueryOptions(userId, token),
			),
		]);

		return { userId, token, orgId };
	},
	pendingComponent: OrganizationPendingComponent,
	component: OrganizationLayout,
	errorComponent: OrganizationErrorPage,
});

function OrganizationPendingComponent() {
	return (
		<div className="min-h-screen bg-base-100 flex items-center justify-center">
			<LoadingSpinner size="lg" />
		</div>
	);
}

function OrganizationLayout() {
	const { orgId } = Route.useParams();
	const navigate = useNavigate();
	const { userId, token } = Route.useLoaderData();
	const { data: accessProfile } = useSuspenseQuery(
		fetchAccessProfileQueryOptions(userId, token, orgId),
	);

	// If we cannot derive org context, bounce back to org selection
	useEffect(() => {
		if (!orgId) {
			navigate({ to: "/app/select-organization" });
		}
	}, [orgId, navigate]);

	const organizationName = accessProfile.orgName ?? orgId;
	const { subscriptionStatus } = accessProfile;

	const isActiveSubscription =
		subscriptionStatus === PlatformSubscriptionStatus.ACTIVE ||
		subscriptionStatus === PlatformSubscriptionStatus.TRIALING;

	if (!isActiveSubscription) {
		return (
			<OrganizationInactive
				organizationName={organizationName}
				orgId={orgId}
				subscriptionStatus={subscriptionStatus}
			/>
		);
	}

	return (
		<OrgBaseLayout organizationName={organizationName} profile={accessProfile}>
			<Outlet />
		</OrgBaseLayout>
	);
}

function OrganizationErrorPage({ error }: { error: Error }) {
	const navigate = useNavigate();

	const handleBack = () => {
		navigate({ to: "/app/select-organization" });
	};

	const handleHome = () => {
		navigate({ to: "/app/select-organization" });
	};

	const handleRetry = () => {
		window.location.reload();
	};

	return (
		<ErrorPage
			error={error}
			showBackButton={true}
			showHomeButton={true}
			showRetryButton={true}
			onBack={handleBack}
			onHome={handleHome}
			onRetry={handleRetry}
		/>
	);
}
